import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Clock, X, Check } from "lucide-react";

interface CustomTimePickerProps {
  value: string; // e.g. "09:00 AM" or "17:00"
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

const PRESET_TIMES = [
  "08:00 AM", "09:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM",
  "04:00 PM", "05:00 PM", "06:00 PM", "07:00 PM",
  "08:00 PM", "09:00 PM", "10:00 PM", "11:00 PM"
];

const CustomTimePicker: React.FC<CustomTimePickerProps> = ({
  value,
  onChange,
  placeholder = "Select time",
  className = "",
  style = {},
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, width: 290 });

  const parseTime = (valStr: string) => {
    if (!valStr) return { hour: "09", minute: "00", period: "AM" };

    const match12 = valStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match12) {
      const h = match12[1].padStart(2, "0");
      const m = match12[2];
      const p = match12[3].toUpperCase();
      return { hour: h, minute: m, period: p };
    }

    const match24 = valStr.match(/^(\d{1,2}):(\d{2})$/);
    if (match24) {
      let hNum = parseInt(match24[1], 10);
      const m = match24[2];
      let p = "AM";
      if (hNum >= 12) {
        p = "PM";
        if (hNum > 12) hNum -= 12;
      }
      if (hNum === 0) hNum = 12;
      return { hour: String(hNum).padStart(2, "0"), minute: m, period: p };
    }

    return { hour: "09", minute: "00", period: "AM" };
  };

  const parsed = parseTime(value);
  const [selectedHour, setSelectedHour] = useState(parsed.hour);
  const [selectedMinute, setSelectedMinute] = useState(parsed.minute);
  const [selectedPeriod, setSelectedPeriod] = useState(parsed.period);

  useEffect(() => {
    if (value) {
      const p = parseTime(value);
      setSelectedHour(p.hour);
      setSelectedMinute(p.minute);
      setSelectedPeriod(p.period);
    }
  }, [value]);

  const updatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popoverWidth = Math.min(290, window.innerWidth - 32);
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth - 16) {
        left = Math.max(16, window.innerWidth - popoverWidth - 16);
      }
      setPopoverPos({
        top: rect.bottom + window.scrollY + 6,
        left: left + window.scrollX,
        width: popoverWidth,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
    }
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        const popoverEl = document.getElementById("v3-timepicker-popover-portal");
        if (popoverEl && popoverEl.contains(e.target as Node)) return;
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleApplyCustomTime = (h: string, m: string, p: string) => {
    setSelectedHour(h);
    setSelectedMinute(m);
    setSelectedPeriod(p);
    const formatted = `${h}:${m} ${p}`;
    onChange(formatted);
  };

  const handleSelectPreset = (preset: string) => {
    onChange(preset);
    setIsOpen(false);
  };

  const formattedDisplay = value ? value : "";

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", ...style }}>
      <style>{`
        .v3-popover-card {
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          box-shadow: 0 20px 48px rgba(0, 0, 0, 0.15) !important;
        }
        html.night-mode .v3-popover-card,
        .night-mode .v3-popover-card {
          background: rgba(18, 18, 28, 0.98) !important;
          border: 1px solid rgba(255, 255, 255, 0.12) !important;
          box-shadow: 0 20px 48px rgba(0, 0, 0, 0.7) !important;
        }
      `}</style>

      {/* Trigger Input Box */}
      <div
        className={`v3-input ${className}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Clock size={15} color="#EE3F7C" />
          <span style={{ fontSize: 13, color: formattedDisplay ? "var(--v3-text-main)" : "var(--v3-text-faint)" }}>
            {formattedDisplay || placeholder}
          </span>
        </div>
        {value && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
            }}
            style={{
              background: "none",
              border: "none",
              color: 'var(--v3-text-faint)',
              cursor: "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Popover Dropdown Panel rendered via Portal */}
      {isOpen &&
        createPortal(
          <div
            id="v3-timepicker-popover-portal"
            className="custom-timepicker-popover v3-popover-card"
            style={{
              position: "absolute",
              top: popoverPos.top,
              left: popoverPos.left,
              zIndex: 999999,
              width: popoverPos.width,
              maxWidth: "calc(100vw - 32px)",
              backdropFilter: "blur(24px) saturate(180%)",
              WebkitBackdropFilter: "blur(24px) saturate(180%)",
              borderRadius: 16,
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 14,
              boxSizing: "border-box",
            }}
          >
            {/* Custom Time Selector Wheels */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              {/* Hours */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--v3-text-faint)', textAlign: "center" }}>
                  HOUR
                </span>
                <select
                  className="v3-input"
                  value={selectedHour}
                  onChange={(e) => handleApplyCustomTime(e.target.value, selectedMinute, selectedPeriod)}
                  style={{
                    padding: "6px 8px",
                    fontSize: 13,
                    fontWeight: 700,
                    textAlign: "center",
                    background: 'var(--v3-bg-subtle)',
                  }}
                >
                  {Array.from({ length: 12 }).map((_, i) => {
                    const h = String(i + 1).padStart(2, "0");
                    return <option key={h} value={h} style={{ background: "var(--v3-bg-subtle, #12121c)", color: 'var(--v3-text-main)' }}>{h}</option>;
                  })}
                </select>
              </div>

              <span style={{ fontSize: 16, fontWeight: 700, color: "#EE3F7C", marginTop: 14 }}>:</span>

              {/* Minutes */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--v3-text-faint)', textAlign: "center" }}>
                  MINUTE
                </span>
                <select
                  className="v3-input"
                  value={selectedMinute}
                  onChange={(e) => handleApplyCustomTime(selectedHour, e.target.value, selectedPeriod)}
                  style={{
                    padding: "6px 8px",
                    fontSize: 13,
                    fontWeight: 700,
                    textAlign: "center",
                    background: 'var(--v3-bg-subtle)',
                  }}
                >
                  {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"].map((m) => (
                    <option key={m} value={m} style={{ background: "var(--v3-bg-subtle, #12121c)", color: 'var(--v3-text-main)' }}>{m}</option>
                  ))}
                </select>
              </div>

              {/* AM / PM Toggle */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--v3-text-faint)', textAlign: "center" }}>
                  PERIOD
                </span>
                <div style={{ display: "flex", background: 'var(--v3-bg-subtle)', padding: 2, borderRadius: 8, marginTop: 2 }}>
                  {["AM", "PM"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => handleApplyCustomTime(selectedHour, selectedMinute, p)}
                      style={{
                        flex: 1,
                        padding: "5px 0",
                        fontSize: 11,
                        fontWeight: 700,
                        borderRadius: 6,
                        border: "none",
                        cursor: "pointer",
                        background: selectedPeriod === p ? "linear-gradient(135deg, #EE3F7C 0%, #F79B59 100%)" : "transparent",
                        color: selectedPeriod === p ? "#fff" : "var(--v3-text-muted)",
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Preset Time Quick Chips */}
            <div style={{ borderTop: "1px solid var(--v3-border-subtle)", paddingTop: 10 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--v3-text-faint)', display: "block", marginBottom: 8 }}>
                POPULAR EVENT TIMES
              </span>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6, maxHeight: 130, overflowY: "auto" }}>
                {PRESET_TIMES.map((preset) => {
                  const isSelected = value === preset;
                  return (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => handleSelectPreset(preset)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "6px 10px",
                        fontSize: 11,
                        fontWeight: isSelected ? 700 : 500,
                        borderRadius: 8,
                        border: isSelected ? "1px solid #EE3F7C" : "1px solid var(--v3-border-subtle)",
                        background: isSelected ? "rgba(238, 63, 124, 0.15)" : "var(--v3-bg-subtle)",
                        color: isSelected ? "#EE3F7C" : "var(--v3-text-main)",
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      <span>{preset}</span>
                      {isSelected && <Check size={12} color="#EE3F7C" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomTimePicker;
